import type {
  Address,
  GatherTaskDropOff,
  GatherTaskPickup,
  OrderDelivery,
  OrderPickup,
  TourDetailsResponse,
  TourStop,
  TourTaskState,
} from "../../api/types";

import { combineBulkTasks } from "../bulk";
import {
  isGatherTaskDropOff,
  isGatherTaskPickup,
  isOrderDelivery,
  isOrderPickup,
  isTruthyAndNotEmpty,
} from "../guards";
import { InternalTourStop, InternalTourTask, TourDetails } from "../types";

export function getAddressString(address: Address) {
  return `${address.street} ${address.number}, ${address.postcode} ${address.place} `;
}

export function createTour(
  response: TourDetailsResponse,
  stopCount: number,
  startLocation: string,
  endLocation: {
    name: string;
    address: string;
  },
): TourDetails {
  return {
    id: response.id,
    name: response.name,
    state: response.state,
    vehicle: response.vehicle,
    stopCount,
    startLocation,
    endLocation,
  };
}

/**
 * Prepare tour stop list for the UI by mapping the tour tasks to the tour stops
 * and filtering out the tasks that are not order pickups, order deliveries,
 * gather task pickups or gather task dropoffs.
 *
 * @param items - List of tour tasks from server
 * @param stops - List of tour stops from server
 */
export function prepareTourStopList(
  tasks: TourDetailsResponse["tasks"],
  stops: TourDetailsResponse["tourStops"],
): InternalTourStop[] {
  const internalTourStops: Array<InternalTourStop> = [];

  stops.forEach((tourStop) => {
    if (!tourStop) {
      return;
    }

    const items = tourStop.tourTaskIds
      .map((taskId) => {
        const task = tasks.find((task) => task.id === taskId);

        if (!task) {
          return;
        }

        if (isOrderPickup(task) || isOrderDelivery(task)) {
          return mapOrderToInternalTourTask(task);
        }

        if (isGatherTaskPickup(task) || isGatherTaskDropOff(task)) {
          return mapGatherTaskToInternalTourTask(task);
        }

        return;
      })
      .filter(isTruthyAndNotEmpty);

    const groupedItems = combineBulkTasks(items);

    internalTourStops.push(createInternalTourStop(tourStop, groupedItems));
  });

  return internalTourStops;
}

export function determineTourStopState(
  items: InternalTourTask[],
): TourTaskState {
  const taskStates = items.map((item) => item.state);

  const allTasksCanceled = taskStates.every((state) => state === "canceled");
  const allTasksDone = taskStates.every((state) => state === "done");
  const someTasksDone = taskStates.some((state) => state === "done");
  const hasNextUp = taskStates.some((state) => state === "nextUp");
  const hasLoaded = taskStates.some((state) => state === "loaded");
  const hasFailed = taskStates.some((state) => state === "failed");

  // Priority order: failed > canceled > nextUp > loaded > done > scheduled
  if (hasFailed) {
    return "failed";
  }

  if (allTasksCanceled) {
    return "canceled";
  }

  if (hasNextUp) {
    return "nextUp";
  }

  if (hasLoaded) {
    return "loaded";
  }

  if (allTasksDone) {
    return "done";
  }

  if (someTasksDone) {
    return "done";
  }

  return "scheduled";
}

function createInternalTourStop(
  data: TourStop,
  tasks: InternalTourTask[],
): InternalTourStop {
  const tourStopType = tasks[0]?.type;
  const pharmacyName = tasks[0]?.pharmacyName;

  return {
    type: tourStopType,
    id: crypto.randomUUID(),
    items: tasks,
    address: getAddressString(data.address),
    aditionalAddress: data.address.floor,
    index: data.index ?? undefined,
    state: determineTourStopState(tasks),
    pharmacyName,
    tags: data.tags,
  };
}

function mapGatherTaskToInternalTourTask(
  task: GatherTaskPickup | GatherTaskDropOff,
): InternalTourTask {
  return {
    type: task.taskType,
    id: task.id,
    parentId: task.gatherTask,
    price: task.paymentCollected,
    name: task.name,
    state: task.state,
    note: task.pharmacyNote,
    pharmacyName: isGatherTaskDropOff(task) ? task.originName : undefined,
    bulkInfo: task.bulkGatherTask,
  };
}

function mapOrderToInternalTourTask(
  task: OrderPickup | OrderDelivery,
): InternalTourTask {
  return {
    type: task.taskType,
    id: task.id,
    parentId: task.order,
    price: task.orderDetails.openAmount,
    name: task.name,
    state: task.state,
    note: task.pharmacyNote,
    pharmacyName: isOrderPickup(task) ? task.originName : undefined,
    phoneNumber: task.orderDetails.recipientPhone,
    mobileNumber: task.orderDetails.recipientMobilePhone,
    isUrgent: task.orderDetails.urgent,
    cooling: task.orderDetails.cooling,
    isBTM: task.orderDetails.isBTM,
    jobNr: task.orderDetails.externalId,
    bulkInfo: task.bulkOrder,
  };
}

export function formatPrice(price: number) {
  const formattedPrice = (price / 100).toFixed(2).replace(".", ",");

  return formattedPrice;
}

function isEveryNonCanceledItemLoaded(stop: InternalTourStop) {
  const nonCanceledItems = stop.items.filter(
    (item) => item.state !== "canceled",
  );
  return (
    nonCanceledItems.length > 0 &&
    nonCanceledItems.every((item) => item.state === "loaded")
  );
}

export function isStopCompleted(stop: InternalTourStop) {
  return stop.state === "done" || stop.state === "canceled";
}

function allPreviousStopsFinished(
  currentStop: InternalTourStop,
  stops?: InternalTourStop[],
): boolean {
  const currentIndex = stops?.findIndex((stop) => stop.id === currentStop.id);

  return (
    stops?.slice(0, currentIndex).every((stop) => isStopCompleted(stop)) ?? true
  );
}

export function isTourStopProcessable({
  currentStop,
  tourState,
  stops,
  shouldProcessInOrder,
}: {
  currentStop: InternalTourStop | undefined;
  tourState: string;
  stops?: InternalTourStop[];
  shouldProcessInOrder?: boolean;
}) {
  const isTourOngoing = tourState === "ongoing";

  if (!isTourOngoing || !currentStop) {
    return false;
  }

  if (shouldProcessInOrder && !allPreviousStopsFinished(currentStop, stops)) {
    return false;
  }

  if (
    currentStop.type === "order-delivery" &&
    currentStop.state === "scheduled"
  ) {
    const hasLoadedItems = currentStop.items.some((item) => {
      if (item.isBulkGroup && item.bulkTasks) {
        return item.bulkTasks.some((bulkTask) => bulkTask.state === "loaded");
      }

      return item.state === "loaded";
    });

    if (!hasLoadedItems) {
      return false;
    }
  }

  if (
    currentStop.type === "gatherTask-dropOff" &&
    currentStop.state === "scheduled" &&
    !isEveryNonCanceledItemLoaded(currentStop)
  ) {
    const allItemsLoaded = currentStop.items.every((item) => {
      if (item.isBulkGroup && item.bulkTasks) {
        return item.bulkTasks.every((bulkTask) => bulkTask.state === "loaded");
      }

      return item.state === "loaded";
    });

    if (!allItemsLoaded) {
      return false;
    }
  }

  return true;
}
